import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';

export type PasscodeView =
  | { kind: 'alreadySet' }
  | { kind: 'create'; passcode: string }
  | {
      kind: 'verify';
      passcode: string;
      confirmation: string;
      hasMismatch: boolean;
    }
  | { kind: 'submitError' }
  | { kind: 'success' };

interface PasscodeState {
  // Null until the user acts; the effective view is then derived from the GET
  // status instead (see usePasscodeFlow), so no action needs to seed this from
  // server data.
  view: PasscodeView | null;
}

export const passcodeInitialState: PasscodeState = {
  view: null,
};

const passcodeSlice = createSlice({
  name: 'passcode',
  initialState: passcodeInitialState,
  reducers: {
    startCreate: (state) => {
      state.view = { kind: 'create', passcode: '' };
    },
    setCreateValue: (state, action: PayloadAction<string>) => {
      state.view = { kind: 'create', passcode: action.payload };
    },
    startVerify: (state, action: PayloadAction<{ passcode: string }>) => {
      state.view = {
        kind: 'verify',
        passcode: action.payload.passcode,
        confirmation: '',
        hasMismatch: false,
      };
    },
    setVerifyValue: (
      state,
      action: PayloadAction<{ passcode: string; confirmation: string }>
    ) => {
      state.view = {
        kind: 'verify',
        passcode: action.payload.passcode,
        confirmation: action.payload.confirmation,
        hasMismatch: false,
      };
    },
    showMismatch: (state, action: PayloadAction<{ passcode: string }>) => {
      state.view = {
        kind: 'verify',
        passcode: action.payload.passcode,
        confirmation: '',
        hasMismatch: true,
      };
    },
    setSuccess: (state) => {
      state.view = { kind: 'success' };
    },
    showSubmitError: (state) => {
      state.view = { kind: 'submitError' };
    },
  },
});

export const {
  startCreate,
  setCreateValue,
  startVerify,
  setVerifyValue,
  showMismatch,
  setSuccess,
  showSubmitError,
} = passcodeSlice.actions;

export const selectPasscodeView = (state: RootState): PasscodeView | null =>
  state.passcode.view;

export default passcodeSlice.reducer;
